import React, { useState, useRef, useEffect } from "react";
import Dropdown from "@/components/ui/Dropdown";
import { RefreshCw } from "lucide-react";

const FILTER_DEBOUNCE_MS = 1000;

const FilterInputs = ({
  processedApplications,
  columns,
  activeFilters,
  filters,
  setFilters,
  setActiveFilters,
  filterInputTriggerClasses = "",
  containerClasses = "",
}) => {
  // Draft values for text + multi-select so UI updates immediately
  const [localFilters, setLocalFilters] = useState({});
  const debounceTimersRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Keep local draft in sync with committed filters (reset / remove / external updates)
  useEffect(() => {
    setLocalFilters((prev) => {
      const next = { ...prev };

      Object.keys(next).forEach((key) => {
        if (!activeFilters?.includes(key)) {
          delete next[key];
        }
      });

      activeFilters?.forEach((key) => {
        // Don't overwrite a value the user is still editing
        if (debounceTimersRef.current[key]) return;

        if (filters[key] !== undefined && filters[key] !== null) {
          next[key] = filters[key];
        } else {
          delete next[key];
        }
      });

      return next;
    });
  }, [filters, activeFilters]);

  const scheduleFilterCommit = (field, value) => {
    if (debounceTimersRef.current[field]) {
      clearTimeout(debounceTimersRef.current[field]);
    }

    debounceTimersRef.current[field] = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
      delete debounceTimersRef.current[field];
    }, FILTER_DEBOUNCE_MS);
  };

  const getUniqueValues = (key) => {
    if (!processedApplications || !Array.isArray(processedApplications)) {
      return [];
    }
    const values = processedApplications.map((app) => app[key]);
    return [...new Set(values)].filter(Boolean);
  };

  const handleMultiFilterChange = (field, selected) => {
    const selectedItems = Array.isArray(selected)
      ? selected
      : selected
        ? [selected]
        : [];
    const selectedValues = selectedItems.map((item) =>
      typeof item === "object" && item !== null ? item.value : item,
    );

    setLocalFilters((prev) => ({
      ...prev,
      [field]: selectedValues,
    }));

    scheduleFilterCommit(field, selectedValues);
  };

  const handleFilterChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));

    scheduleFilterCommit(field, value);
  };

  const resetFilters = () => {
    Object.values(debounceTimersRef.current).forEach(clearTimeout);
    debounceTimersRef.current = {};
    setLocalFilters({});
    setFilters({});
    setActiveFilters([]);
  };

  return (
    <div className={`flex flex-wrap gap-2 items-center ${containerClasses}`}>
      {activeFilters && activeFilters.length > 0 && (
        <button
          onClick={resetFilters}
          className="p-2 hover:bg-muted rounded-full"
          title="Reset all filters"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      )}
      {activeFilters &&
        activeFilters.map((filterKey) => {
          const column = columns.find((col) => col.key === filterKey);
          const inputType = column?.inputType || "text";

          const uniqueValues = filterKey.startsWith("field_")
            ? [
                ...new Set(
                  (processedApplications || [])
                    .map(
                      (app) => app.fields?.[filterKey.replace("field_", "")],
                    )
                    .filter(Boolean),
                ),
              ]
            : getUniqueValues(filterKey);

          uniqueValues.sort((a, b) => {
            const aLabel = String(a).toLowerCase();
            const bLabel = String(b).toLowerCase();
            return aLabel.localeCompare(bLabel);
          });

          const dropdownOptions = column?.options
            ? [
                ...column?.options?.map((value) => {
                  if (typeof value === "object") {
                    return {
                      label: value.label,
                      value: value.value,
                    };
                  }
                  return {
                    label: String(value),
                    value: value,
                  };
                }),
              ]
            : [];

          const currentSelected =
            localFilters[filterKey] ?? filters[filterKey] ?? [];
          const selectedValues = Array.isArray(currentSelected)
            ? currentSelected
            : currentSelected
              ? [currentSelected]
              : [];
          const selectedOptions = dropdownOptions?.filter((opt) =>
            selectedValues.includes(opt.value),
          );

          const textValue =
            localFilters[filterKey] ?? filters[filterKey] ?? "";

          return (
            <div key={filterKey} className="min-w-[160px] md:min-w-[200px]">
              {inputType === "select" ||
              inputType === "radio" ||
              inputType === "checkbox" ? (
                <Dropdown
                  options={dropdownOptions?.filter((opt) => opt.value !== "")}
                  selectedValues={selectedOptions}
                  onChange={(selected) =>
                    handleMultiFilterChange(filterKey, selected)
                  }
                  placeholder={`${column?.label || filterKey}`}
                  searchable={true}
                  className="w-full"
                  multiSelect={column?.useMultiSelect}
                  renderOption={(option) => (
                    <p className="text-foreground text-sm">{option.label}</p>
                  )}
                  triggerClassName={filterInputTriggerClasses}
                />
              ) : (
                <input
                  type={inputType}
                  className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
                  placeholder={`${column?.label || filterKey}`}
                  value={textValue}
                  onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                />
              )}
            </div>
          );
        })}
    </div>
  );
};

export default FilterInputs;
